import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Link, Redirect, useParams } from 'react-router-dom';
import { useFeathers } from '../../app/util';
import { useSelector } from 'react-redux';
import { TextField, MenuItem, Tooltip, Typography, Select, Accordion, AccordionSummary, AccordionDetails, Checkbox, Alert } from '@mui/material';
import {
  CTAButton,
  SearchableSelectInput,
  CloseButton,
  HintTile,
  PartySelect,
  SearchItemStyle,
  SelectableList,
  CirclePhoto,
  CoveragePlanSelect,
  DistrictSelectInput,
  OfficeSelectInput,
  BallotTextInput
} from '..';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getRaceConfigurationForOffice } from './race-configuration';

const CreateRaceModal = ({
  onClose,
  onSaveCreateRace = true,
  redirectOnFinish,
  onFinish,           // if provided as a function, will pass back the race data to create.
  election: electionGiven,           // the full eleciton object
  electionKey,
  restrictToDistrict, // a district id to restrict the search to descendants of (defaults to election.districts)
  restrictToOfficesWithDistrictTypes, // an array of district types to restrict the search to

  // default props
  coveragePlan
}) => {
  const feathers = useFeathers();

  const headerTitle = useMemo(() => {
    if (!restrictToDistrict?._id) {
      return 'New Race'
    } else {
      return `New Race for ${restrictToDistrict?.longName}`
    }
  }, [restrictToDistrict])

  const [ election, setElection ] = useState(electionGiven);
  const [ stateElectionConfig, setStateElectionConfig ] = useState(null);
  useEffect(() => {
    if(electionKey && feathers && !election) {
      const loadElection = async () => {
        const election = await feathers.getService('elections').get(electionKey);
        setElection(election);
      }
      loadElection();
    }
  }, [electionKey, feathers])

  const isPartisan = election?.partisan;

  const [ formInputs, setFormInputs ] = useState({
    ballotText: null,
    office: null,
    district: null,
    hasPrecedents: null,
    precedents: [],
    winners: [],  // if we have precedents
    party: null,
    duplicate: null,
    additionalDistricts: null,
    hasAdditionalDistricts: null,
    ...getRaceConfigurationForOffice(null),
    positionData: {
      special: false,
      seatIdentifier: null,
      seatIdentifierType: null
    }
  })

  const [ progress, setProgress ] = useState(0);
  const submittable = progress >= 4;

  const onChange = e => {
    const { name, value } = e.target;
    const update = {
      [name]: value,
      ...(name === 'office' ? getRaceConfigurationForOffice(value) : {}),
    };
    
    let newInputs = {
      ...formInputs,
      ...update
    };

    if (name === 'hasAdditionalDistricts' && value === false) {
      newInputs.additionalDistricts = []
    }

    // Maintain the workflow for the inputs
    if(!newInputs.office) {
      setFormInputs({ ...newInputs, ...{ district: null, hasAdditionalDistricts: null,  additionalDistricts: null, hasPrecedents: null, coverage: null, duplicate: null, party: null }})
      setPrecedentOptions([]);
      setProgress(0);
    } else if(!newInputs.district) {
      setFormInputs({ ...newInputs, ...{ hasAdditionalDistricts: null,  additionalDistricts: null, hasPrecedents: null, coverage: null, duplicate: null, party: null }})
      setProgress(1);
      setPrecedentOptions([]);
    } else {
      // now we do some more nuanced work
      let precedents;
      if(newInputs.hasPrecedents === null) {
        precedents = Boolean(election?.runoffPrecedent || election?.primaryPrecedent);
      } else {
        precedents = newInputs.hasPrecedents;
      }
      newInputs.hasPrecedents = precedents;
      if(precedents === true) {
        // in this section, we want to check if the user has selected a precedent
        if(!newInputs.precedents.length) {
          // if we're setting the hasPrecedents directly
          setFormInputs({ ...newInputs, duplicate: null, party: null, precedents: [], winners: [] })
          setProgress(2);
        } else {
          const winnersToFoldIn = name === 'precedents' ? newInputs.precedents.map(race => {
            const winners = Object.entries(race?.result?.byCandidate || []).filter(([ _id, c ]) => ['winner', 'runoff'].includes(c.status))
            return winners.map(w => w[0])
          }).flat() : [];

          const winners = [].concat(
            newInputs.winners || [],
            winnersToFoldIn
          );
 
          // now dedup by candidate id
          const winnersFullIdsUnique = [...new Set(winners)];

          newInputs.winners = winnersFullIdsUnique;

          // if we're setting the winners / precedents directly
          setFormInputs(newInputs)
          setProgress(newInputs?.winners?.length > 0 ? 4 : 3);
        }
      } else if (precedents === false) {
        if(isPartisan && !newInputs.party) {
          setFormInputs({ ...newInputs, ...{ duplicate: null }})
          setProgress(3);
        } else {
          setFormInputs(newInputs)
          setProgress(4);
        }
      }
    } 
  }

  const descendantOf = useMemo(
    () => (restrictToDistrict
      ? (restrictToDistrict?._id || restrictToDistrict)
      : election?.districts),
    [election, restrictToDistrict]
  )

  const [ precedentOptions, setPrecedentOptions ] = useState([]);
  const [ precedentLoading, setPrecedentLoading ] = useState(false);

  const loadPrecedentOptions = async () => {
    if(!feathers) return;
    setPrecedentLoading(true);

    const precedentElections = [...new Set([election.runoffPrecedent, election.primaryPrecedent])].filter(Boolean);

    const raceOptions = await feathers.getService('races').find({
      query: {
        election: precedentElections,
        office: formInputs.office?.key,
        district: formInputs.district?._id,
        $limit: 100
      }
    })

    if(!raceOptions.data.length) {
      setFormInputs({ ...formInputs, hasPrecedents: false })
    } else {
      const options = raceOptions.data.map(({ candidates, ...d }) => ({ ...d, text: d.longName }))
      setPrecedentOptions(options);
      setPrecedentLoading(false);
    }
  }

  const winnerOptionsAll = useMemo(() => {
    if(!formInputs.precedents.length) return [];
    return formInputs.precedents.map(race => {
      const candidateIds = race.candidateSummary.ids || [];
      const candidatesFull = candidateIds.map((_id, idx) => {
        return {
          _id,
          name: race.candidateSummary.names[idx],
          photo: race.candidateSummary.photos[idx],
          hasVariants: race.candidateSummary.photoHasVariants?.[idx] === true,
        }
      })
      return candidatesFull
    }).flat();
  }, [ formInputs.precedents ])

  useEffect(() => {
    if(formInputs.hasPrecedents && !precedentLoading && !precedentOptions.length) {
      loadPrecedentOptions();
    }
  }, [ formInputs, precedentOptions, precedentLoading ])

  // Load state-election-config for this election (if available) to restrict parties for partisan races
  useEffect(() => {
    const loadStateElectionConfig = async () => {
      if (!feathers || !election) {
        setStateElectionConfig(null);
        return;
      }

      try {
        // election.stateCode is available from the typed Election model
        const stateCode = election.stateCode || null;
        if (!stateCode) {
          setStateElectionConfig(null);
          return;
        }

        const config = await feathers.getService('state-election-configurations').get(stateCode.toUpperCase());
        setStateElectionConfig(config || null);
      } catch (err) {
        console.error('Error loading state election configuration for CreateRaceModal:', err);
        setStateElectionConfig(null);
      }
    };

    loadStateElectionConfig();
  }, [feathers, election]);

  /* manage searching options for selection, based on search text */
  const [ optionsForField, setOptionsForField ] = useState({});
  const [ loadingForField, setLoadingForField ] = useState({});

  const searchForField = async (field, searchText, addtlParams) => {
    if(!feathers) return;

    const loadingUpdate = {};
    loadingUpdate[field] = true;
    setLoadingForField({ ...loadingForField, ...loadingUpdate });

    let options = [];
    const officeToReference = addtlParams?.office || formInputs?.office;
    if(field === 'election') {
      const elections = await feathers.getService('elections').find({ query: {
        ...(searchText ? { name: { $search: searchText } } : {}),
        $sort: { date: -1 }
      }})
      options = elections.data.map(({ name, key }) => ({ text: name, key }));
    }  else if(['district', 'additionalDistricts'].includes(field) && officeToReference) {
      const districtTypes = officeToReference
        ? officeToReference.districtTypes.includes("country")
          ? ["state"]
          : (officeToReference.districtTypes || [])
        : null;

      const districts = await feathers.getService('districts').find({
        query: {
          ...(searchText ? { searchTerm: { $search: searchText } } : {}),
          ...(districtTypes ? { type: districtTypes } : {}),
          ...(descendantOf ? { $descendantOf: descendantOf, $nestChildren: false, $includeAncestor: true } : {}),
          $sort: { number: 1 }
        },
      })

      options = districts.data.map(({ longName, _id, votingType }) => ({ text: `${longName}${votingType === 'representative-district' ? ' (at-large)' : ''}`, _id }))
      if (field === 'additionalDistricts') {
        options = options.filter(d => d._id !== formInputs.district?._id)
      }
    }

    const updateForOptions = {};
    updateForOptions[field] = options;
    setOptionsForField({ ...optionsForField, ...updateForOptions })

    const loadingUpdate2 = {};
    loadingUpdate2[field] = false;
    setLoadingForField({ ...loadingForField, ...loadingUpdate2 });

    return options;
  }

  const [ submitting, setSubmitting ] = useState(false);
  const [ raceCreated, setRaceCreated ] = useState(false);
  const [ duplicateRace, setDuplicateRace ] = useState(false);
  const [ error, setError ] = useState(false);
  const submit = async () => {
    if(!submittable || submitting) return;

    let mappedInputs = {};
    if(formInputs.hasPrecedents) {
      if(!formInputs.precedents || !formInputs.winners) {
        throw new Error('Cannot create')
      }
      // check if the inputs are primary or runoff

      const inputsArePrimary = election.primaryPrecedent //&& formInputs.precedents.some(race => race.party);
      const inputsAreRunoff = election.runoffPrecedent && formInputs.precedents?.length === 1;
      if(!inputsArePrimary && !inputsAreRunoff) {
        throw new Error('Cannot determine if inputs are primary or runoff')
      }
      if(inputsArePrimary) {
        mappedInputs = {
          election: election.key,
          primaryPrecedent: formInputs.precedents.map(raceFull => {
            const winnersForRace = formInputs.winners.filter(winnerId => raceFull.candidateSummary.ids.includes(winnerId))
            return {
              ...raceFull,
              winners: winnersForRace
            }
          }),
          entityMatchInputs: [ formInputs.ballotText ],
          $suppress409: formInputs?.duplicate,
          rankedChoice: formInputs.rankedChoice,
        }
      } else if(inputsAreRunoff) {
        mappedInputs = {
          election: election.key,
          runoffPrecedent: {
            _id: formInputs.precedents[0]?._id,
            winners: formInputs.winners
          },
          entityMatchInputs: [ formInputs.ballotText ],
          rankedChoice: formInputs.rankedChoice,
        }
      }
    } else {
      mappedInputs = {
        election: election?.key,
        office: formInputs?.office?.key,
        district: formInputs?.district?._id,
        additionalDistricts: formInputs?.additionalDistricts?.map(d => d._id),
        coveragePlan: formInputs?.manualCoveragePlan,
        maxChoices: formInputs?.maxChoices,
        retention: formInputs?.retention,
        ...(isPartisan ? { party: formInputs?.party } : {}),
        $suppress409: formInputs?.duplicate,
        timestamp: new Date().toISOString(), // since this is not immediately being saved to the db -- a way to uniquely identify the race for the time being
        candidates: [],
        entityMatchInputs: [ formInputs.ballotText ],
        rankedChoice: formInputs.rankedChoice,
      }
    }

    if(formInputs.positionData?.seatIdentifier || formInputs.positionData?.special) {
      mappedInputs.positionData = {
        ...(formInputs.positionData?.special ? { special: formInputs.positionData.special } : {}),
        ...(formInputs.positionData?.seatIdentifier && { seatIdentifier: formInputs.positionData.seatIdentifier }),
        ...(formInputs.positionData?.seatIdentifierType && { seatIdentifierType: formInputs.positionData.seatIdentifierType })
      }
    }

    setSubmitting(true);
    setError(false);

    try {
      const race = await feathers.getService('races').create(mappedInputs);
      setRaceCreated({
        id: race._id,
        election: race.election
      })
      if(onFinish) onFinish(race);
    } catch(err) {
      // Is error code a duplicate error?

      const errorAsJson = err.toJSON()
      if(errorAsJson.code === 409) {
        setDuplicateRace(errorAsJson)
      } else {
        setError(errorAsJson)
      }
      setSubmitting(false);
    }
  }

  const [ stageUpdate, setStageUpdate ] = useState(null);
  useEffect(() => {
    if(stageUpdate) {
      setStageUpdate(null);
      onChange(stageUpdate)
    }
  }, [ stageUpdate ])

  const loadInitialOptions = async () => {
    // on initial load, load the options for different fields, selecting defaults if there's only 1 option
    if(!feathers) return;

    const fieldOpts = await searchForField('office', '');
    if(fieldOpts.length !== 1) return;
    const office = fieldOpts[0];
    setStageUpdate({ target: { name: 'office', value: office }})
    // setFormInputs({ ...formInputs, office })
    // setProgress(1)

    // now for district
    const districtOpts = await searchForField('district', '', { office });
    if(districtOpts.length !== 1) return;
    const district = districtOpts[0];
    setStageUpdate({ target: { name: 'district', value: district }})

    // if(coveragePlan) {
    //   setStageUpdate({ target: { name: 'coveragePlan', value: coveragePlan }})    
    // }
    // setFormInputs({ ...formInputs, office, district, ...(coveragePlan ? { coverage: coveragePlan } : {}) })
    // setProgress(coveragePlan ? 4 : 3)
  }

  useEffect(() => {
    if(feathers) {
      loadInitialOptions();
    }
  }, [ feathers ])

  const handleBallotTextChange = ({ ballotText, district: matchedDistrict, office: matchedOffice, positionData: matchedPositionData }) => {
    if(matchedDistrict && matchedOffice) {
      setProgress(4);
    } else if(matchedOffice) {
      setProgress(2);
    }
    setFormInputs(formInputsPrev => {
      const updates = { ballotText };
      if (matchedDistrict && !formInputsPrev.district) {
        updates.district = {
          ...matchedDistrict,
          text: matchedDistrict.longName
        }
      }
      if (matchedOffice && !formInputsPrev.office) {
        updates.office = {
          ...matchedOffice,
          text: matchedOffice.name
        }
        Object.assign(updates, getRaceConfigurationForOffice(matchedOffice));
      }
      if (matchedPositionData) {
        updates.positionData = {
          special: matchedPositionData.special || false,
          ...(matchedPositionData.seatIdentifier && { seatIdentifier: matchedPositionData.seatIdentifier }),
          ...(matchedPositionData.seatIdentifierType && { seatIdentifierType: matchedPositionData.seatIdentifierType })
        }
      }
      return {
        ...formInputsPrev,
        ...updates
      }
    })
  };


  const districtRow = () => (
    <Row>
      <div className='label'><Typography variant='body1' style={{ whiteSpace: 'nowrap' }}>District</Typography></div>
      <DistrictSelectInput
        value={formInputs.district}
        onChange={onChange}
        descendantOf={descendantOf}
        style={{ flexGrow: 1 }}
        districtTypes={formInputs.office?.districtTypes}
      />
    </Row>
  );

  const precedentsPossible = (election?.runoffPrecedent || election?.primaryPrecedent)

  const errorMessage = useMemo(() => {
    if (!error) return null;
    if (typeof error === 'string') return error;
    if (typeof error === 'object') {
      if (error?.type === 'FeathersError') {
        let messageToShow;
        if (error?.errors && Object.values(error.errors).length > 0) {
          messageToShow = Object.values(error.errors).join(' ');
        } else {
          messageToShow = error?.message;
        }

        switch (error.code) {
          case 400:
            if (messageToShow) return messageToShow;
            return 'There was a problem with the data you submitted. Please check the form and try again.';
          case 401:
          case 403:
            if (messageToShow) return messageToShow;
            return 'You are not authorized to perform this action.';
          default:
            return messageToShow;
        }
      } else if (
        error.message &&
        typeof error.message === 'string' &&
        !error.message.includes('Object')
      ) {
        return error.message;
      } else {
        return JSON.stringify(error);
      }
    }

    return 'There was an error when creating the race. Please try again!';
  }, [error]);

  if(raceCreated && redirectOnFinish) {
    return <Redirect to={`/elections/${raceCreated.election?.key ?? raceCreated.election}/races/${raceCreated.id}`} />
  }
  
  return (
    <Wrapper>
      <CloseButton
        onClick={onClose}
        style={{
          position: 'absolute',
          height: '36px',
          right: '-20px',
          top: '-20px'
        }}
      />
      <Top>
        <h1>{headerTitle}</h1>
        <Row>
          <div className='label' style={{ display: 'flex', flexWrap: 'nowrap', gap: '8px'}}>
            <Typography variant='body1' style={{ whiteSpace: 'nowrap' }}>Ballot text</Typography>
            <Tooltip title='How this item exactly appears on the ballot, including the district'>
              <InfoOutlined style={{ fontSize: '20px', marginTop: '2px' }}/>
            </Tooltip>
          </div>
          <BallotTextInput
            value={formInputs.ballotText}
            onChange={handleBallotTextChange}
            boundingDistrict={restrictToDistrict?._id || restrictToDistrict || election?.districts?.[0]}
          />
        </Row>
        <Row>
          <div className='label'><Typography variant='body1' style={{ whiteSpace: 'nowrap' }}>Office</Typography></div>
          <OfficeSelectInput
            value={formInputs.office}
            onChange={onChange}
            style={{ flexGrow: 1 }}
            restrictToOfficesWithDistrictTypes={restrictToOfficesWithDistrictTypes}
            descendantOf={descendantOf}
          />
        </Row>
        {
          (progress >= 1) &&
          districtRow()
        }
        {
          progress >= 2 && (
            <PositionDataSection>
              <Row>
                <div className='label'><Typography variant='body1' style={{ whiteSpace: 'nowrap' }}>Special Election</Typography></div>
                <Checkbox
                  checked={formInputs.positionData?.special || false}
                  onChange={(e) => {
                    setFormInputs({
                      ...formInputs,
                      positionData: {
                        ...formInputs.positionData,
                        special: e.target.checked
                      }
                    });
                  }}
                />
              </Row>
              <Row>
                <div className='label'><Typography variant='body1' style={{ whiteSpace: 'nowrap' }}>Seat Identifier</Typography></div>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', flexGrow: 1 }}>
                  <Select
                    size="small"
                    value={formInputs.positionData?.seatIdentifierType || ''}
                    onChange={(e) => {
                      setFormInputs({
                        ...formInputs,
                        positionData: {
                          ...formInputs.positionData,
                          seatIdentifierType: e.target.value || null
                        }
                      });
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="incumbentName">Incumbent</MenuItem>
                    <MenuItem value="seat">Seat</MenuItem>
                    <MenuItem value="post">Post</MenuItem>
                    <MenuItem value="division">Division</MenuItem>
                    <MenuItem value="position">Position</MenuItem>
                  </Select>
                  <TextField
                    size="small"
                    value={formInputs.positionData?.seatIdentifier || ''}
                    onChange={(e) => {
                      setFormInputs({
                        ...formInputs,
                        positionData: {
                          ...formInputs.positionData,
                          seatIdentifier: e.target.value || null
                        }
                      });
                    }}
                    placeholder={`Enter ${formInputs.positionData?.seatIdentifierType || 'seat'} identifier`}
                  />
                </div>
              </Row>
            </PositionDataSection>
          )
        }
        {
          progress >= 2 && (precedentsPossible) &&
          <Row>
            <div className='label'><Typography variant='body1' style={{ whiteSpace: 'nowrap' }}>Has Previous</Typography></div>
            <TextField select name='hasPrecedents' onChange={onChange} style={{ flexGrow: 1 }} value={formInputs?.hasPrecedents}>
              <MenuItem value={true}>Yes</MenuItem>
              <MenuItem value={false}>No</MenuItem>
            </TextField>
          </Row>
        }
        {
          (progress >= 2 && formInputs.hasPrecedents) &&
          <Row>
            <div className='label' style={{ display: 'flex', flexWrap: 'nowrap', gap: '8px'}}>
              <Typography variant='body1' style={{ whiteSpace: 'nowrap' }}>Copy from</Typography>
              <Tooltip title='If this race has a primary election(s) in our system, select them to merge and copy the data'><InfoOutlined style={{ fontSize: '20px', marginTop: '2px' }}/></Tooltip>
            </div>
            <SearchableSelectInput
              multi={true}
              name='precedents'
              searchOptionComponent={PrecedentSearchItem}
              onChangeSearchText={(searchText) => console.log('doing nothing with searchText', searchText)}
              value={formInputs.precedents}
              onChange={onChange}
              optionsLoading={precedentLoading}
              options={precedentOptions || []}
              style={{ flexGrow: 1 }}
            />
          </Row>
        }
        {
          (progress >= 3 && formInputs.precedents?.length > 0) &&
          <Row>
            <div className='label' style={{  alignSelf: 'flex-start', paddingTop: '30px' }}><Typography variant='body1' style={{ whiteSpace: 'nowrap', }}>Candidates<br/>to copy</Typography></div>
            <SelectableList
              items={winnerOptionsAll}
              selectedIds={formInputs.winners}
              idField='_id'
              onChange={(winners) => onChange({ target: { name: 'winners', value: winners }})}
              listItemComponent={candidate => {
                return (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <CirclePhoto size='small' photoPath={candidate.photo} hasVariants={candidate.hasVariants === true} />
                    <Typography variant='body1'>{candidate.name}</Typography>
                  </div>
                )
              }}
            />
          </Row>
        }
        {
          (progress >= 2 && (formInputs.hasPrecedents === false || !precedentsPossible)) &&
          <Row>
            <div className='label'><Typography variant='body1' style={{ whiteSpace: 'nowrap' }}>Coverage plan</Typography></div>
            <CoveragePlanSelect
              manualValue={formInputs?.manualCoveragePlan}
              onChangeCoveragePlan={(coveragePlan) => onChange({ target: { name: 'manualCoveragePlan', value: coveragePlan }})}
              district={formInputs?.district}
              office={formInputs?.office}
              election={election}
              style={{ flexGrow: 1 }}
            />
          </Row>
        }
        {
          (progress >= 3 && isPartisan && (formInputs.hasPrecedents === false || !precedentsPossible)) &&
          <Row>
            <div className='label'><Typography variant='body1' style={{ whiteSpace: 'nowrap', }}>Party</Typography></div>
            <PartySelect
              size="small"
              value={formInputs.party}
              style={{ flexGrow: 1 }}
              partyOptions={stateElectionConfig?.supportedPartiesForPrimary}
              onChange={(party) => onChange({ target: { name: 'party', value: party } })}
            />
          </Row>
        }
        {
          ((progress >= 2 && !isPartisan) || (progress >= 3 && isPartisan)) &&
          <AdditionalOptions>
            {optionsForField.district?.length > 1 && (
            <Row>
              <div className='label'><Typography variant='body1' style={{ }}>Additional districts</Typography></div>
              <SearchableSelectInput
                multi={true}
                name='additionalDistricts'
                onChangeSearchText={(searchText) => searchForField('additionalDistricts', searchText)}
                value={formInputs.additionalDistricts}
                onChange={onChange}
                optionsLoading={loadingForField.additionalDistricts}
                options={optionsForField?.additionalDistricts || []}
                style={{ flexGrow: 1 }}
              />
          </Row>
            )}
            <Row>
              <div className='label'><Typography variant='body1' style={{ }}>Retention election</Typography></div>
              <Select name='retention' onChange={onChange} style={{ flexGrow: 1 }} value={formInputs?.retention}>
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </Row>
            <Row>
              <div className='label'><Typography variant='body1' style={{ }}>Max Choices</Typography></div>
              <Select
                name='maxChoices'
                onChange={onChange}
                style={{ flexGrow: 1 }}
                value={formInputs?.maxChoices}
              >
                {Array.from({ length: 25 }, (_, i) => i + 1).map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </Select>
            </Row>
            <Row>
              <div className='label'><Typography variant='body1' style={{ }}>Ranked Choice</Typography></div>
              <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={formInputs.rankedChoice.enabled}
                    onChange={(e) => {
                      setFormInputs({
                        ...formInputs,
                        rankedChoice: {
                          ...formInputs.rankedChoice,
                          enabled: e.target.checked
                        }
                      });
                    }}
                  />
                  <Typography variant='body2'>Enable ranked choice voting</Typography>
                </div>
                {formInputs.rankedChoice.enabled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '32px' }}>
                    <Typography variant='body2'>Max choices:</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={formInputs.rankedChoice.maxChoices}
                      onChange={(e) => {
                        setFormInputs({
                          ...formInputs,
                          rankedChoice: {
                            ...formInputs.rankedChoice,
                            maxChoices: parseInt(e.target.value) || 1
                          }
                        });
                      }}
                      inputProps={{ min: 1, max: 10 }}
                      style={{ width: '80px' }}
                    />
                  </div>
                )}
              </div>
            </Row>
          </AdditionalOptions>
        }
        {
          (error && progress >= 4) &&
          <Alert severity="error" style={{ marginBottom: '20px' }}>
            {errorMessage}
          </Alert>
        }
        {
          (duplicateRace && progress >= 4) &&
          <HintTile style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span>
                <b>Heads up! </b>
                There is already a{' '}
                {
                  duplicateRace?.data?.raceId
                    ? (
                      <Link
                        to={`/elections/${election?.key ?? electionKey}/races/${duplicateRace.data.raceId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formInputs?.office?.text} race
                      </Link>
                    )
                    : `${formInputs?.office?.text} race`
                }
                {` created for ${formInputs?.district?.text} for this election. Do you mean to create another?`}
              </span>
              <div style={{ display: 'flex', flexDirection: 'row', marginTop: '12px' }}>
                <Checkbox
                  checked={formInputs?.duplicate}
                  onClick={() => onChange({ target: { name: 'duplicate', value: !(formInputs.duplicate === true) }})}
                />
                <span style={{ marginLeft: '8px' }}><b>Yes, proceed anyways</b></span>
              </div>
            </div>
          </HintTile>
        }
      </Top>
      <CTAButton
        value={submitting ? `Creating...` : 'Create'}
        disabled={!submittable || submitting}
        onClick={submit}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 500px;
  min-height: 200px;
`
const Top = styled.div`
  display: flex;
  flex-direction: column;

  h1 {
    font-size: 28px;
    margin: 0 0 26px;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  margin-bottom: 28px;

  div.label {
    font-size: 18px;
    margin: 0 14px 0 0;
    opacity: 0.7;
    width: 100px;
  }
`;

const PrecedentSearchItem = (race) => {
  return (
    <SearchItemStyle>
      <div>{race.longName}, {race.election?.name || race.election.split('-').map(elSeg => elSeg.slice(0,1).toUpperCase() + elSeg.slice(1)).join(' ')}</div>
      <div className='subtext'>{(race.candidateSummary.names || []).join(', ')}</div>
    </SearchItemStyle>
  )
}

const AdditionalOptions = ({ children }) => (
  <Accordion
    sx={{
      mb: 4,
      boxShadow: 'none',
      '&:before': {
        display: 'none',
      },
      '& .MuiAccordionSummary-root': {
        padding: '0 16px',
        minHeight: '48px',
      },
      '& .MuiAccordionDetails-root': {
        padding: '0 16px 16px',
      },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{
        '&:hover': {
          backgroundColor: 'transparent',
        },
      }}
    >
      <Typography variant="body2" color="text.secondary">Additional Options</Typography>
    </AccordionSummary>
    <AccordionDetails>
      {children}
    </AccordionDetails>
  </Accordion>
);

const PositionDataSection = styled.div`
  margin-bottom: 0px;
`;

export { CreateRaceModal };
